import { create } from 'zustand';
import alumniService from '../api/alumniService';
import logger from '../utils/logger';

// ponytail: Zustand store replacing the AlumniContext boilerplate
export const useAlumniStore = create((set, get) => ({
  successStories: [],
  testimonials: [],
  statistics: [],
  registrations: [],
  storySubmissions: [],
  loading: true,
  error: null,

  fetchSuccessStories: async (params = {}) => {
    try {
      set({ loading: true });
      const response = await alumniService.getSuccessStories(params);
      if (response.status === 'success') {
        set({ successStories: response.data.stories || [], error: null });
      }
    } catch (err) {
      logger.error('Error fetching success stories:', err);
      set({ error: err.response?.data?.message || 'Failed to load success stories' });
    } finally {
      set({ loading: false });
    }
  },

  fetchTestimonials: async (params = {}) => {
    try {
      set({ loading: true });
      const response = await alumniService.getTestimonials(params);
      if (response.status === 'success') {
        set({ testimonials: response.data.testimonials || [], error: null });
      }
    } catch (err) {
      logger.error('Error fetching testimonials:', err);
      set({ error: err.response?.data?.message || 'Failed to load testimonials' });
    } finally {
      set({ loading: false });
    }
  },

  fetchStatistics: async (params = {}) => {
    try {
      set({ loading: true });
      const response = await alumniService.getStatistics(params);
      if (response.status === 'success') {
        set({ statistics: response.data.statistics || [], error: null });
      }
    } catch (err) {
      logger.error('Error fetching statistics:', err);
      set({ error: err.response?.data?.message || 'Failed to load statistics' });
    } finally {
      set({ loading: false });
    }
  },

  fetchRegistrations: async (params = {}) => {
    try {
      set({ loading: true });
      const response = await alumniService.getRegistrations(params);
      set({ registrations: response.data.registrations || [], error: null });
    } catch (err) {
      logger.error('Error fetching registrations:', err);
      set({ error: err.response?.data?.message || 'Failed to load registrations' });
    } finally {
      set({ loading: false });
    }
  },

  addSuccessStory: async (formData) => {
    const response = await alumniService.createSuccessStory(formData);
    set((state) => ({ successStories: [...state.successStories, response.data.story] }));
    return response.data.story;
  },

  updateSuccessStory: async (id, formData) => {
    const response = await alumniService.updateSuccessStory(id, formData);
    set((state) => ({
      successStories: state.successStories.map((s) => (s._id === id ? response.data.story : s)),
    }));
    return response.data.story;
  },

  deleteSuccessStory: async (id) => {
    await alumniService.deleteSuccessStory(id);
    set((state) => ({ successStories: state.successStories.filter((s) => s._id !== id) }));
  },

  submitSuccessStory: async (formData) => {
    const response = await alumniService.submitSuccessStory(formData);
    return response.data;
  },

  approveStorySubmission: async (id) => {
    const response = await alumniService.approveStorySubmission(id);
    set((state) => ({
      successStories: [...state.successStories, response.data.story],
      storySubmissions: state.storySubmissions.filter((s) => s._id !== id),
    }));
  },

  addTestimonial: async (formData) => {
    const response = await alumniService.createTestimonial(formData);
    set((state) => ({ testimonials: [...state.testimonials, response.data.testimonial] }));
    return response.data.testimonial;
  },

  updateTestimonial: async (id, formData) => {
    const response = await alumniService.updateTestimonial(id, formData);
    set((state) => ({
      testimonials: state.testimonials.map((t) => (t._id === id ? response.data.testimonial : t)),
    }));
    return response.data.testimonial;
  },

  deleteTestimonial: async (id) => {
    await alumniService.deleteTestimonial(id);
    set((state) => ({ testimonials: state.testimonials.filter((t) => t._id !== id) }));
  },

  addStatistic: async (data) => {
    const response = await alumniService.createStatistic(data);
    set((state) => ({ statistics: [...state.statistics, response.data.statistic] }));
    return response.data.statistic;
  },

  updateStatistic: async (id, data) => {
    const response = await alumniService.updateStatistic(id, data);
    set((state) => ({
      statistics: state.statistics.map((s) => (s._id === id ? response.data.statistic : s)),
    }));
    return response.data.statistic;
  },

  deleteStatistic: async (id) => {
    await alumniService.deleteStatistic(id);
    set((state) => ({ statistics: state.statistics.filter((s) => s._id !== id) }));
  },

  registerAlumni: async (data) => {
    return await alumniService.registerAlumni(data);
  },

  addRegistration: async (data) => {
    const response = await alumniService.createRegistration(data);
    set((state) => ({ registrations: [...state.registrations, response.data.registration] }));
    return response.data.registration;
  },

  updateRegistration: async (id, data) => {
    const response = await alumniService.updateRegistration(id, data);
    set((state) => ({
      registrations: state.registrations.map((r) => (r._id === id ? response.data.registration : r)),
    }));
    return response.data.registration;
  },

  deleteRegistration: async (id) => {
    await alumniService.deleteRegistration(id);
    set((state) => ({ registrations: state.registrations.filter((r) => r._id !== id) }));
  },

  addStorySubmission: async (data) => {
    const response = await alumniService.createStorySubmission(data);
    set((state) => ({ storySubmissions: [...state.storySubmissions, response.data.submission] }));
    return response.data.submission;
  },

  updateStorySubmission: async (id, data) => {
    const response = await alumniService.updateStorySubmission(id, data);
    set((state) => ({
      storySubmissions: state.storySubmissions.map((s) => (s._id === id ? response.data.submission : s)),
    }));
    return response.data.submission;
  },

  deleteStorySubmission: async (id) => {
    await alumniService.deleteStorySubmission(id);
    set((state) => ({ storySubmissions: state.storySubmissions.filter((s) => s._id !== id) }));
  },

  getActiveSuccessStories: () => get().successStories.filter((s) => s.isActive),
  getFeaturedSuccessStories: () => get().successStories.filter((s) => s.isActive && s.featured),
  getActiveTestimonials: () => get().testimonials.filter((t) => t.status === 'approved' || t.status === 'Active' || t.isActive),
  getFeaturedTestimonials: () => get().testimonials.filter((t) => t.featured),
  getActiveStatistics: () => get().statistics.filter((s) => s.isActive),
}));

// ponytail: init driven by PublicDataLoader — not here

